import { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import Slider from 'react-slick';
import { twMerge } from 'tailwind-merge';

import { game_banners, GAMES, GameStatusTag, IGame } from './constants/games.constants';

import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';

import GameBanner from './gameBanner/GameBanner';
import GameHero from './gameHero/GameHero';
import GamesCard from './gamesCard/GamesCard';
import styles from './GamesRoute.module.scss';
import { gameHasPoolId } from './utils';

enum GameType {
  All = 'All',
  ReadyToPlay = 'Ready To Play',
  StakingAvailable = 'Staking Available',
  ComingSoon = 'Coming Soon',
}

const gameTypes = [GameType.All, GameType.ReadyToPlay, GameType.StakingAvailable, GameType.ComingSoon];

const renderGameCount = (games: IGame[], type: GameType) => {
  const adapt = {
    [GameType.All]: games.length,
    [GameType.ReadyToPlay]: games.filter((game) => game.status === GameStatusTag.Live).length,
    [GameType.StakingAvailable]: games.filter((game) => game.status === GameStatusTag.StakingOpen).length,
    [GameType.ComingSoon]: games.filter((game) => game.status === GameStatusTag.ComingSoon).length,
  };

  return adapt[type] ?? 0;
};

const filterGames = (games: IGame[], gameType: GameType) => {
  const adapt = {
    [GameType.All]: games,
    [GameType.ReadyToPlay]: games.filter((game) => game.status === GameStatusTag.Live),
    [GameType.StakingAvailable]: games.filter((game) => game.status === GameStatusTag.StakingOpen),
    [GameType.ComingSoon]: games.filter((game) => game.status === GameStatusTag.ComingSoon),
  };

  return adapt[gameType] ?? null;
};

const GamesRoute = () => {
  const [games, setGames] = useState<IGame[]>(GAMES);
  const [gameType, setGameType] = useState<GameType>(GameType.All);

  useEffect(() => {
    setGames(
      GAMES.map((game) =>
        game.invest && gameHasPoolId(game) && game.href !== 'reality-rush'
          ? { ...game, status: !game.open ? GameStatusTag.StakingClosed : GameStatusTag.StakingOpen }
          : game
      )
    );
  }, []);

  return (
    <Page pageBackground="dark">
      <Scrollbars style={{ width: '100%', height: '100%' }}>
        <PageWrapper landingFooter homeFooter={false}>
          <GameHero />
          <div className="tw-mx-auto tw-mb-10 md:tw-mb-20 md:tw-mt-10 md:tw-max-w-screen-md lg:tw-max-w-screen-lg xl:tw-max-w-screen-xl">
            <Slider
              dots
              speed={300}
              rows={1}
              arrows={false}
              infinite={false}
              dotsClass="slick-dots"
              className={styles['slider']}
              slidesToShow={1}
              useTransform={false}
            >
              {game_banners.map((banner, idx) => (
                <GameBanner key={idx} {...banner} />
              ))}
            </Slider>
          </div>
          <div className="tw-mx-auto tw-flex tw-flex-col tw-gap-10 tw-px-5 tw-py-10 md:tw-max-w-screen-md lg:tw-max-w-screen-lg lg:tw-gap-20 lg:tw-px-0 xl:tw-max-w-screen-xl">
            <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
              <h1 className="tw-mb-5 tw-text-[32px]/[28px] tw-uppercase tw-text-white lg:tw-mb-10 lg:tw-text-5xl">
                GAMES POWERED BY <span className="tw-text-turquoise">RMV</span> AND{' '}
                <span className="tw-text-turquoise">DIGITAL ASSETS</span> ECONOMY
              </h1>
              <div className="tw-flex tw-w-screen tw-gap-3 tw-overflow-x-auto tw-px-4 tw-pb-4 min-[576px]:tw-w-auto min-[576px]:tw-justify-center min-[576px]:tw-p-0">
                {gameTypes.map((type) => (
                  <div
                    key={type}
                    className={twMerge(
                      'tw-flex tw-h-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-3xl tw-px-2.5 lg:tw-px-5',
                      gameType === type ? 'tw-bg-white' : 'tw-bg-backgroundColorLight'
                    )}
                    onClick={() => setGameType(type)}
                  >
                    <span
                      className={twMerge(
                        'tw-whitespace-nowrap tw-px-2 tw-font-semibold tw-leading-6 lg:tw-text-xl',
                        gameType === type ? 'tw-text-backgroundColorDark' : 'tw-text-white'
                      )}
                    >
                      {type}&nbsp;{renderGameCount(games, type)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3 lg:tw-gap-7">
              {filterGames(games, gameType).map((game) => (
                <GamesCard key={game.href} game={game} />
              ))}
            </div>
          </div>
        </PageWrapper>
      </Scrollbars>
    </Page>
  );
};

export default GamesRoute;
